// ACTIVITY METRICS — the arithmetic behind /performance/activity, with no React
// and no network so it can be run under plain node (scripts/test-activityMetrics.mjs).
//
// Every number the page shows is computed HERE from rows the page also shows,
// so a tile can never disagree with the table under it. The rules are written
// out in plain English next to the code that applies them because the page is
// investor-facing: the reader has to be able to check the claim.
//
// Days are America/Chicago calendar days everywhere — Bo works Central Time,
// the cadence engine (lib/cadence.js) and the mail history (lib/mailHistory.js)
// already bucket on that calendar, and the HubSpot metrics route buckets the
// same way, so the two halves of the Days ledger agree. Intl does the zone
// arithmetic; there is no timezone library in this app and there will not be
// one for a single zone.

export const CHICAGO_TZ = 'America/Chicago'

// The three daily operations, by the ids the chat page logs in
// operation_run.data.operationId (lib/agentRun.js OPERATIONS). A day of "all 3"
// means one recorded run of EACH of these — not three runs of one.
export const OPERATION_IDS = ['initial-outreach-50', 'followup-50', 'reply-open-threads']
export const OPERATION_LABELS = {
  'initial-outreach-50': 'Initial outreach',
  'followup-50': 'Daily Tasks',
  'reply-open-threads': 'Follow-Up',
}

const DAY_MS = 86400000

const CHI_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: CHICAGO_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const CHI_PARTS_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: CHICAGO_TZ,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

const CHI_CLOCK_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: CHICAGO_TZ,
  hour: 'numeric',
  minute: '2-digit',
})

// ── timestamps ──────────────────────────────────────────────────────────────

// ISO string, epoch-ms number, or a digit string of epoch ms → ms, else null.
// Mirrors the server's _when_ms: anything unparseable is null, and the caller
// counts it as undatable rather than bucketing it somewhere it did not happen.
export function whenMs(value) {
  if (value == null || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const s = String(value).trim()
  if (/^\d{10,}$/.test(s)) return Number(s)
  const ms = Date.parse(s)
  return Number.isNaN(ms) ? null : ms
}

// 'YYYY-MM-DD' on the Chicago calendar, '' when the value is not a time.
export function chicagoDayOf(value) {
  const ms = whenMs(value)
  if (ms == null) return ''
  try {
    return CHI_DAY_FMT.format(new Date(ms))
  } catch {
    return ''
  }
}

// 'h:mm AM' as read on a Chicago clock — for the ops columns of the ledger.
export function chicagoClock(value) {
  const ms = whenMs(value)
  if (ms == null) return ''
  try {
    return CHI_CLOCK_FMT.format(new Date(ms))
  } catch {
    return ''
  }
}

// Milliseconds that Chicago wall time is AHEAD of UTC at this instant
// (negative: -6h in CST, -5h in CDT). Derived from Intl rather than a table so
// the DST rule can never drift from the browser's own zone data.
function chicagoOffsetMs(ms) {
  const parts = {}
  for (const p of CHI_PARTS_FMT.formatToParts(new Date(ms))) parts[p.type] = p.value
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  )
  return asUtc - Math.floor(ms / 1000) * 1000
}

function splitDay(dayKey) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey || ''))
  if (!m) return null
  return [Number(m[1]), Number(m[2]), Number(m[3])]
}

// The UTC instant at which this Chicago day begins. Two passes: the offset at
// the naive UTC midnight is usually right, but on a DST transition day the
// 02:00 switch means the offset AT the real midnight can differ from the guess,
// so the second pass re-reads the offset at the instant we landed on.
export function chicagoMidnightMs(dayKey) {
  const parts = splitDay(dayKey)
  if (!parts) return null
  const naive = Date.UTC(parts[0], parts[1] - 1, parts[2])
  const guess = naive - chicagoOffsetMs(naive)
  return naive - chicagoOffsetMs(guess)
}

export function chicagoMidnightISO(dayKey) {
  const ms = chicagoMidnightMs(dayKey)
  return ms == null ? '' : new Date(ms).toISOString()
}

// ── day-key arithmetic (pure calendar, no zones involved) ───────────────────

function dayToUtcNoon(dayKey) {
  const p = splitDay(dayKey)
  return p ? Date.UTC(p[0], p[1] - 1, p[2], 12) : NaN
}

function utcToDay(ms) {
  return new Date(ms).toISOString().slice(0, 10)
}

export function addDays(dayKey, n) {
  const ms = dayToUtcNoon(dayKey)
  return Number.isNaN(ms) ? '' : utcToDay(ms + n * DAY_MS)
}

// 0 = Sunday … 6 = Saturday, for a calendar day key.
export function weekdayOf(dayKey) {
  const ms = dayToUtcNoon(dayKey)
  return Number.isNaN(ms) ? -1 : new Date(ms).getUTCDay()
}

export function isBusinessDay(dayKey) {
  const w = weekdayOf(dayKey)
  return w >= 1 && w <= 5
}

export function previousBusinessDay(dayKey) {
  let d = addDays(dayKey, -1)
  while (d && !isBusinessDay(d)) d = addDays(d, -1)
  return d
}

export function nextBusinessDay(dayKey) {
  let d = addDays(dayKey, 1)
  while (d && !isBusinessDay(d)) d = addDays(d, 1)
  return d
}

// Monday of the ISO week holding this day — the weekly chart bucket.
export function weekStartOf(dayKey) {
  const w = weekdayOf(dayKey)
  if (w < 0) return ''
  return addDays(dayKey, -((w + 6) % 7))
}

// 'Aug 22' from a day key, with no Date parsing that could shift the day.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function shortDay(dayKey) {
  const p = splitDay(dayKey)
  return p ? `${MONTHS[p[1] - 1]} ${p[2]}` : ''
}

export function longDay(dayKey) {
  const p = splitDay(dayKey)
  return p ? `${MONTHS[p[1] - 1]} ${p[2]}, ${p[0]}` : ''
}

// ── ranges ──────────────────────────────────────────────────────────────────

// The presets the page offers, in menu order. 'custom' is the only key that
// needs arguments (a from/to pair of Chicago day keys) — see rangeFor.
export const RANGE_KEYS = ['today', 'yesterday', 'week', 'lastweek', 'month', '3m', 'all', 'custom']

// Keys this page used to offer. They are NOT in the menu any more, but a link
// somebody saved, pasted into a deck or mailed to an investor still carries
// ?range=30d, and quietly re-pointing that link at today would change the
// numbers under a claim that has already been made. So rangeFor keeps
// computing them and the page keeps showing them when they arrive.
export const LEGACY_RANGE_KEYS = ['30d', '90d', 'ytd']

// RANGE_LABELS.custom is the MENU entry only. A resolved custom range is
// labelled with its own dates ("Mar 3 – Mar 17, 2026") by dayRangeLabel,
// because "Custom" above a table of numbers tells the reader nothing.
export const RANGE_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This week',
  lastweek: 'Last week',
  month: 'This month',
  '3m': 'Past 3 months',
  all: 'All time',
  custom: 'Custom Date',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  ytd: 'This year',
}

// TODAY, not a month of history. The page is opened to answer "did the routine
// run today", so that is what it opens on; every other window is one click away
// and lives in the URL.
export const DEFAULT_RANGE_KEY = 'today'

export function isRangeKey(key) {
  return RANGE_KEYS.includes(key) || LEGACY_RANGE_KEYS.includes(key)
}

// A real calendar day key. The round-trip through Date.UTC is what rejects
// '2026-02-30' and '2026-13-01' — a regex alone would accept both and the page
// would then chart a day that does not exist.
export function isDayKey(value) {
  const p = splitDay(value)
  if (!p) return false
  return utcToDay(Date.UTC(p[0], p[1] - 1, p[2])) === String(value)
}

// The same day-of-month n months away, clamped into a shorter month
// (May 31 − 3 months → Feb 28). Rolling over into the next month instead would
// make "past 3 months" quietly claim days the reader did not ask for.
export function addMonths(dayKey, n) {
  const p = splitDay(dayKey)
  if (!p) return ''
  const total = p[0] * 12 + (p[1] - 1) + n
  const year = Math.floor(total / 12)
  const month = total - year * 12
  const lastOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  return utcToDay(Date.UTC(year, month, Math.min(p[2], lastOfMonth), 12))
}

// 'Mar 3 – Mar 17, 2026' — the year is said once when both ends share it, and
// twice when they do not, so a range spanning New Year cannot be misread.
export function dayRangeLabel(fromDay, toDay) {
  if (!fromDay || !toDay) return ''
  if (fromDay === toDay) return longDay(fromDay)
  return fromDay.slice(0, 4) === toDay.slice(0, 4)
    ? `${shortDay(fromDay)} – ${longDay(toDay)}`
    : `${longDay(fromDay)} – ${longDay(toDay)}`
}

// ── the calendar page's own date controls (2026-09-16) ─────────────────────
// The calendar is opened to look at days, so it has to be able to move between
// them without a trip back to the summary. Two pure rules feed its controls.

/**
 * The URL patch that makes exactly ONE Chicago day the page's range.
 *
 * Named presets keep their names — today, yesterday — so the link stays the
 * canonical one and the label stays a word rather than a date; any other day
 * is a one-day custom window. ?day= is dropped: a one-day range has no day
 * left to pick. Null for a day that is not real, or is in the future.
 */
export function singleDayRangeParams(day, todayDay) {
  if (!isDayKey(day) || !isDayKey(todayDay) || day > todayDay) return null
  if (day === todayDay) return { range: 'today', from: null, to: null, day: null }
  if (day === addDays(todayDay, -1)) return { range: 'yesterday', from: null, to: null, day: null }
  return { range: 'custom', from: day, to: day, day: null }
}

/**
 * Where the timeline's day stepper can go, and what each move writes to the
 * URL.
 *
 *   range     a rangeFor() frame — only sinceDay/untilDay are read.
 *   day       the day on the timeline (already clamped by timelineDayOf).
 *   todayDay  the Chicago day "now" falls on.
 *
 * Returns { single, back, forward, pick }. `back` and `forward` are URL
 * patches, or null when that step is not possible; `pick(day)` is the patch
 * for a day chosen in the date field, or null when it cannot be shown.
 *
 * Inside a range wider than a day the stepper WALKS THE RANGE and stops at
 * its edges: the range is the filter, and the picker above the charts is the
 * way to widen it. On a one-day range the range IS the day, so stepping moves
 * the whole window — yesterday, the day before, and forward again up to
 * today — which is what a calendar's arrows are expected to do. Before this
 * the arrows only existed inside a wider range, so the page opened on Today
 * with no way to look at any other day.
 */
export function dayStepPlan(range, day, todayDay) {
  const sinceDay = (range && range.sinceDay) || ''
  const untilDay = (range && range.untilDay) || ''
  const on = isDayKey(day) ? day : untilDay
  if (sinceDay && sinceDay === untilDay) {
    return {
      single: true,
      back: on ? singleDayRangeParams(addDays(on, -1), todayDay) : null,
      forward: on && on < todayDay ? singleDayRangeParams(addDays(on, 1), todayDay) : null,
      pick: (d) => singleDayRangeParams(d, todayDay),
    }
  }
  return {
    single: false,
    back: on && (!sinceDay || on > sinceDay) ? { day: addDays(on, -1) } : null,
    forward: on && untilDay && on < untilDay ? { day: addDays(on, 1) } : null,
    // The field's own min/max already fence the range; a typed day outside it
    // is pulled back by timelineDayOf when it is read.
    pick: (d) => (isDayKey(d) ? { day: d } : null),
  }
}

// The shape every range returns: day keys for the HubSpot metrics route (which
// takes Chicago days) and ISO instants for the events ledger (which takes
// instants). `until` is the last millisecond of untilDay, NOT of today — a
// 'yesterday' range that ran to tonight would count events it does not show.
// `notice` is plain English for the reader whenever the page had to alter what
// the URL asked for; '' the rest of the time.
function rangeFrame(key, label, sinceDay, untilDay, notice = '') {
  return {
    key,
    label,
    sinceDay,
    untilDay,
    since: sinceDay ? chicagoMidnightISO(sinceDay) : null,
    until: new Date(chicagoMidnightMs(addDays(untilDay, 1)) - 1).toISOString(),
    notice,
  }
}

// A custom from/to pair, straightened out in the open.
//
// The rule is that this function never returns a window the caller did not ask
// for WITHOUT saying so in `notice`. A reversed pair, a date in the future or a
// missing half are all things a hand-edited or hand-shared link really does
// carry; each one is corrected to the nearest honest window and named, because
// the alternative — rendering a table under dates that are not the dates that
// produced it — is exactly the silent wrong number this page exists to avoid.
function customRange(fromRaw, toRaw, today) {
  const notes = []
  let from = isDayKey(fromRaw) ? String(fromRaw) : null
  let to = isDayKey(toRaw) ? String(toRaw) : null
  if (fromRaw && !from) notes.push(`the start date (${String(fromRaw)}) is not a real calendar day`)
  if (toRaw && !to) notes.push(`the end date (${String(toRaw)}) is not a real calendar day`)

  if (!from && !to) {
    // Nothing usable to compute from. Falling back to a WIDE window here would
    // put a month of numbers on screen that nobody asked for; today is the
    // smallest, least surprising claim, and the notice says the dates went.
    return rangeFrame(
      DEFAULT_RANGE_KEY,
      RANGE_LABELS[DEFAULT_RANGE_KEY],
      today,
      today,
      `Custom range: ${notes.length ? notes.join(' and ') : 'no dates were given'}, so this is today instead.`,
    )
  }
  // Each fallback clause has to be true of the link that was actually given.
  // A date that was PRESENT but unreadable has already been named two lines
  // up ("the end date (nonsense) is not a real calendar day"); following that
  // with "no end date was given" in the same sentence tells the reader
  // something they can see is false, and a notice with one false clause in it
  // is a notice nobody believes — which defeats the whole point of saying the
  // window was corrected at all. So the "none was given" wording is used only
  // when none really was.
  if (!from) {
    from = to
    notes.push(fromRaw ? 'the range is that single day instead' : 'no start date was given, so the range is that single day')
  }
  if (!to) {
    to = today
    notes.push(toRaw ? 'the range runs to today instead' : 'no end date was given, so the range runs to today')
  }
  if (to > today) {
    notes.push(`the end date was in the future and was pulled back to today (${longDay(today)})`)
    to = today
  }
  if (from > today) {
    notes.push(`the start date was in the future and was pulled back to today (${longDay(today)})`)
    from = today
  }
  if (from > to) {
    const swap = from
    from = to
    to = swap
    notes.push('the dates were the wrong way round and were swapped')
  }
  return rangeFrame('custom', dayRangeLabel(from, to), from, to, notes.length ? `Custom range: ${notes.join('; ')}.` : '')
}

/**
 * The window the page is reporting on, as day keys and as instants.
 *
 *   key      one of RANGE_KEYS or LEGACY_RANGE_KEYS; anything else is 'today',
 *            because an unreadable key must not silently widen the claim.
 *   now      the instant "today" is read from (Chicago calendar).
 *   opts     { from, to } day keys, used only by 'custom'.
 *
 * `all` has sinceDay/since null — the caller omits the parameter and the server
 * reads from the beginning. Every other range INCLUDES its untilDay in full.
 */
export function rangeFor(key, now = Date.now(), opts = {}) {
  const today = chicagoDayOf(now)
  const k = isRangeKey(key) ? key : DEFAULT_RANGE_KEY
  if (k === 'custom') return customRange(opts && opts.from, opts && opts.to, today)

  let sinceDay = today
  let untilDay = today
  if (k === 'yesterday') {
    sinceDay = addDays(today, -1)
    untilDay = sinceDay
  } else if (k === 'week') sinceDay = weekStartOf(today) // Monday-start, week to date
  else if (k === 'lastweek') {
    // The WHOLE previous Monday–Sunday, not "the last seven days". A rep asked
    // "how did last week go" means the week that finished, and a rolling
    // seven-day window would mix half of it with half of this one — the two
    // answers differ most on a Monday, which is exactly when the question gets
    // asked. Unlike every other preset this one does not end today, so it is
    // the first window on the page that is COMPLETE: nothing about it can
    // still change, and the "today partial" caveat does not apply.
    const thisMonday = weekStartOf(today)
    sinceDay = addDays(thisMonday, -7)
    untilDay = addDays(thisMonday, -1)
  }
  else if (k === 'month') sinceDay = `${today.slice(0, 7)}-01` // month to date
  else if (k === '3m') sinceDay = addMonths(today, -3) // the same date three months back, inclusive
  else if (k === 'all') sinceDay = null
  else if (k === '30d') sinceDay = addDays(today, -29)
  else if (k === '90d') sinceDay = addDays(today, -89)
  else if (k === 'ytd') sinceDay = `${today.slice(0, 4)}-01-01`
  return rangeFrame(k, RANGE_LABELS[k], sinceDay, untilDay)
}

/**
 * The window operation-run rows have to be read over so that a STREAK can be
 * counted, given the window the page is actually showing.
 *
 *   range       a rangeFor() frame — only sinceDay/untilDay are read.
 *   todayDay    the Chicago day key "now" falls on.
 *   windowDays  how far back a streak is allowed to look (inclusive of today).
 *
 * Returns { sinceDay, untilDay, merged }. `untilDay` is always today: a streak
 * that stopped at the end of a 'yesterday' range would answer a question
 * nobody asked. `merged` says whether this window can double as the range's
 * own read — true only when the range ALSO ends today, in which case the wider
 * of the two windows is returned and the caller filters the range back out of
 * the rows.
 *
 * Why merging is refused otherwise: joining a past range to today produces a
 * read nobody asked for and nobody can afford. A custom March-2020 window
 * would become a 2372-day request, and the ledger pages backwards under a time
 * budget — so it would stop early holding the NEWEST rows and drop the very
 * days the reader picked, leaving the table empty with no honest explanation.
 * Two bounded reads beat one unbounded read of the wrong days.
 */
export function streakWindow(range, todayDay, windowDays) {
  const back = addDays(todayDay, -(Math.max(1, windowDays) - 1))
  const merged = Boolean(range) && range.untilDay === todayDay
  if (!merged) return { sinceDay: back, untilDay: todayDay, merged: false }
  // 'all' (sinceDay null) already covers everything; otherwise take whichever
  // of the two windows reaches further back.
  const sinceDay = range.sinceDay == null ? null : range.sinceDay < back ? range.sinceDay : back
  return { sinceDay, untilDay: todayDay, merged: true }
}

// ── event rows ──────────────────────────────────────────────────────────────

// A row logged while the owner was impersonating this member carries
// data.impersonating. Its Actor is the OWNER (the token is the owner's), so a
// query for the member's actor would not normally return it — but the page
// still refuses to count one on the member's behalf should it ever arrive.
export function isImpersonated(row) {
  return Boolean(row && row.data && row.data.impersonating === true)
}

// Does this operation_run row prove the operation ran on the member's behalf?
//   recorded === true        the run finished and the chat page recorded it
//                            (built something, or confirmed nothing was due)
//   historyUnavailable !== true  a follow-up that could not read the mirror
//                            "confirmed" nothing — that is an admission, not a
//                            finding, so it does not count toward the routine
//   not impersonated         the owner driving Bo's account is not Bo's routine
export function countsAsRun(row) {
  const d = row && row.data
  if (!d || typeof d !== 'object') return false
  if (d.recorded !== true) return false
  if (d.historyUnavailable === true) return false
  if (d.impersonating === true) return false
  return OPERATION_IDS.includes(d.operationId)
}

// Plain-English outcome of an operation_run row, for the Runs table and the
// Operation-runs tile note. 'unreadable' wins over 'nothing' because a
// nothing-to-do with the mirror unread is not a finding.
export function runOutcome(row) {
  const d = (row && row.data) || {}
  if (d.aborted === true) return 'aborted'
  if (d.ok === false) return 'failed'
  if (d.historyUnavailable === true) return 'unreadable'
  if (d.nothingToDo === true) return 'nothing'
  if (d.ok === true) return 'built'
  return 'unknown'
}

export const RUN_OUTCOME_LABELS = {
  built: 'Built',
  nothing: 'Nothing due',
  unreadable: 'Ran, history unreadable',
  failed: 'Failed',
  aborted: 'Stopped',
  unknown: 'Unknown',
}

// ── per-type totals from the server summary ─────────────────────────────────

// eventsSummary().days is { 'YYYY-MM-DD': { '<type>': n, '<type>@impersonated': n } }.
// This splits it: counts[type] (the member's own) and impersonated[type].
export function splitSummaryCounts(dayCounts) {
  const counts = {}
  const impersonated = {}
  for (const [key, n] of Object.entries(dayCounts || {})) {
    const v = Number(n) || 0
    const at = key.indexOf('@impersonated')
    if (at >= 0) impersonated[key.slice(0, at)] = (impersonated[key.slice(0, at)] || 0) + v
    else counts[key] = (counts[key] || 0) + v
  }
  return { counts, impersonated }
}

// ── the day ledger ──────────────────────────────────────────────────────────

/**
 * One row per Chicago day in the range, continuous (zero days included so a
 * gap in the routine is visible as a row, not as a missing row).
 *
 *   summaryDays  eventsSummary().days — per-type counts per day
 *   metrics      hubspotMetrics() body (days[] used) or null when unread
 *   runs         operation_run rows (normalize()d, with data)
 *   copies       draft_copied rows, or null when not loaded (email/subject
 *                split is then unknown — reported as null, never as 0)
 *   opens        hubspot_email_opened rows, or null when not loaded
 *   sinceDay / untilDay  range bounds (sinceDay null = from the first day seen)
 */
export function buildDays({
  summaryDays = {},
  metrics = null,
  runs = [],
  copies = null,
  opens = null,
  sinceDay = null,
  untilDay = null,
  now = Date.now(),
} = {}) {
  const today = chicagoDayOf(now)
  const seen = new Set(Object.keys(summaryDays || {}))
  for (const d of (metrics && metrics.days) || []) if (d && d.day) seen.add(d.day)
  for (const r of runs || []) {
    const d = chicagoDayOf(r && r.createdAt)
    if (d) seen.add(d)
  }
  const sortedSeen = [...seen].filter(Boolean).sort()
  const first = sinceDay || sortedSeen[0] || today
  const last = untilDay || today

  const metricDays = new Map()
  for (const d of (metrics && metrics.days) || []) if (d && d.day) metricDays.set(d.day, d)

  // Ops: the EARLIEST counting run of each operation per day.
  const opsByDay = new Map()
  const runCountByDay = new Map()
  for (const r of runs || []) {
    const day = chicagoDayOf(r && r.createdAt)
    if (!day) continue
    if (!isImpersonated(r)) runCountByDay.set(day, (runCountByDay.get(day) || 0) + 1)
    if (!countsAsRun(r)) continue
    const op = r.data.operationId
    const at = r.createdAt
    const slot = opsByDay.get(day) || {}
    if (!slot[op] || whenMs(at) < whenMs(slot[op])) slot[op] = at
    opsByDay.set(day, slot)
  }

  const copiesByDay = new Map()
  if (Array.isArray(copies)) {
    for (const r of copies) {
      if (isImpersonated(r)) continue
      const day = chicagoDayOf(r && r.createdAt)
      if (!day) continue
      const c = copiesByDay.get(day) || { email: 0, subject: 0, total: 0 }
      const kind = r.data && r.data.kind
      if (kind === 'email') c.email += 1
      else if (kind === 'subject') c.subject += 1
      c.total += 1
      copiesByDay.set(day, c)
    }
  }
  const opensByDay = new Map()
  if (Array.isArray(opens)) {
    for (const r of opens) {
      if (isImpersonated(r)) continue
      const day = chicagoDayOf(r && r.createdAt)
      if (day) opensByDay.set(day, (opensByDay.get(day) || 0) + 1)
    }
  }

  const out = []
  let guard = 0
  for (let day = first; day && day <= last && guard < 20000; day = addDays(day, 1), guard += 1) {
    const { counts, impersonated } = splitSummaryCounts(summaryDays[day])
    const ops = {}
    const slot = opsByDay.get(day) || {}
    for (const id of OPERATION_IDS) ops[id] = slot[id] || null
    const m = metricDays.get(day) || {}
    const c = copiesByDay.get(day)
    const totalEvents = Object.values(counts).reduce((a, b) => a + b, 0)
    const totalImpersonated = Object.values(impersonated).reduce((a, b) => a + b, 0)
    out.push({
      day,
      businessDay: isBusinessDay(day),
      ops,
      allThree: OPERATION_IDS.every((id) => Boolean(ops[id])),
      opsCompleted: OPERATION_IDS.filter((id) => Boolean(ops[id])).length,
      questions: counts.question_asked || 0,
      // From rows when loaded (so the split is exact); from the summary count
      // otherwise, with the split honestly unknown.
      copies: Array.isArray(copies) ? (c ? c.total : 0) : counts.draft_copied || 0,
      copiesEmail: Array.isArray(copies) ? (c ? c.email : 0) : null,
      copiesSubject: Array.isArray(copies) ? (c ? c.subject : 0) : null,
      hubspotOpens: Array.isArray(opens) ? opensByDay.get(day) || 0 : counts.hubspot_email_opened || 0,
      // HubSpot-mirror columns: 0 only when the mirror was actually read and
      // said nothing happened. When `metrics` is null (never read, incomplete,
      // unconfigured) the truth is "unknown", which is null — the table prints
      // a dash and the CSV an empty cell, same as copiesEmail above. A zero
      // here would be a confident claim the page never computed.
      outreach: metrics ? m.outreach_emails || 0 : null,
      outreachCompanies: metrics ? m.outreach_companies || 0 : null,
      followups: metrics ? m.followups || 0 : null,
      replies: metrics ? m.replies || 0 : null,
      responses: metrics ? m.responses || 0 : null,
      deals: metrics ? m.deals || 0 : null,
      tasks: metrics ? m.tasks || 0 : null,
      // Calls, meetings and notes are the same claim under the same rule as
      // the seven columns above: a 0 here means the mirror was read and held
      // none that day, and null means nobody has read it yet. They are the
      // highest-value activity Bo logs — a page that printed 0 calls because
      // the mirror was unread would be its worst possible lie.
      calls: metrics ? m.calls || 0 : null,
      meetings: metrics ? m.meetings || 0 : null,
      notes: metrics ? m.notes || 0 : null,
      signIns: counts.sign_in || 0,
      runs: runCountByDay.get(day) || 0,
      events: totalEvents,
      impersonated: totalImpersonated,
    })
  }
  return out
}

// ── streaks ─────────────────────────────────────────────────────────────────

// Longest run of complete days where "consecutive" is decided by `step`
// (next business day, or next calendar day). skipDays are bridged: they
// neither count nor break.
function longestRun(completeSet, skip, step, minDay, maxDay) {
  const sorted = [...completeSet].sort()
  let longest = 0
  let longestStartedOn = null
  let longestEndedOn = null
  let run = 0
  let runStart = null
  let prev = null
  for (const day of sorted) {
    if (minDay && day < minDay) continue
    if (maxDay && day > maxDay) continue
    let consecutive = false
    if (prev) {
      let n = step(prev)
      let hops = 0
      while (skip.has(n) && hops < 400) {
        n = step(n)
        hops += 1
      }
      consecutive = n === day
    }
    if (consecutive) run += 1
    else {
      run = 1
      runStart = day
    }
    if (run > longest) {
      longest = run
      longestStartedOn = runStart
      longestEndedOn = day
    }
    prev = day
  }
  return { longest, longestStartedOn, longestEndedOn }
}

function streakCore({ days, now, skipDays, basis }) {
  const businessOnly = basis === 'business-days'
  const complete = new Set()
  for (const d of days || []) {
    if (!d || !d.day || !d.allThree) continue
    if (businessOnly && !isBusinessDay(d.day)) continue
    complete.add(d.day)
  }
  const skip = new Set((skipDays || []).filter(Boolean))
  const back = businessOnly ? previousBusinessDay : (d) => addDays(d, -1)
  const forward = businessOnly ? nextBusinessDay : (d) => addDays(d, 1)

  const today = chicagoDayOf(now)
  const todayComplete = complete.has(today)
  // Today counts only when it is done; an unfinished today neither counts nor
  // breaks the streak (Bo may still be working). A weekend "today" starts the
  // walk from Friday on the business basis.
  const todayEligible = businessOnly ? isBusinessDay(today) : true
  const start = todayEligible && todayComplete ? today : back(today)

  let current = 0
  let currentStartedOn = null
  let cursor = start
  let guard = 0
  while (cursor && guard < 20000) {
    guard += 1
    if (skip.has(cursor)) {
      cursor = back(cursor)
      continue
    }
    if (!complete.has(cursor)) break
    current += 1
    currentStartedOn = cursor
    cursor = back(cursor)
  }
  // The first incomplete day the walk hit: when current is 0 that is the day
  // the routine was last missed; when current > 0 it is the day before the
  // streak began (shown only as "since <currentStartedOn>").
  //
  // A ledger with NO complete day anywhere has no "last missed" day to name:
  // the walk stops on its very first step, and reporting that day as the day a
  // streak ended would invent a routine that never ran. Before tracking began
  // — the state every workspace is in on day one — the honest answer is "no
  // complete business day yet", so the day is withheld rather than dressed up.
  const streakEndedOn = complete.size ? cursor || null : null

  const { longest, longestStartedOn, longestEndedOn } = longestRun(complete, skip, forward)
  return {
    current,
    longest: Math.max(longest, current),
    currentStartedOn,
    streakEndedOn,
    longestStartedOn,
    longestEndedOn,
    todayComplete,
    today,
    basis,
  }
}

// Business-day streak — the headline. Weekends never break it; an optional
// skipDays list (holidays) is bridged the same way, explicitly, so a missed
// Monday after Labor Day is a choice the viewer made, not a rule we hid.
export function streaks({ days = [], now = Date.now(), skipDays = [] } = {}) {
  return streakCore({ days, now, skipDays, basis: 'business-days' })
}

// Calendar streak — same shape, every day counts, weekends included.
export function calendarStreak({ days = [], now = Date.now(), skipDays = [] } = {}) {
  return streakCore({ days, now, skipDays, basis: 'calendar' })
}

// ── tiles ───────────────────────────────────────────────────────────────────

function uniq(values) {
  return new Set(values.filter((v) => v !== undefined && v !== null && v !== ''))
}

/** A 0–1 rate off a /metrics totals block; null when absent or not a number. */
export function rateOf(totals, key) {
  const v = totals && Object.prototype.hasOwnProperty.call(totals, key) ? totals[key] : null
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * Delivery and open rates over a run of /metrics day rows (the Dashboard's
 * "this week"): the raw per-day counts are summed and divided once — a rate
 * per day cannot be averaged. Null rates when the denominator is empty, and
 * all-null when the rows predate the sends ledger (no emails_sent key).
 */
export function sendRatesOf(dayRows) {
  const rows = Array.isArray(dayRows) ? dayRows : []
  const known = rows.some((d) => d && Object.prototype.hasOwnProperty.call(d, 'emails_sent'))
  if (!known) return { known: false, sent: null, delivered: null, bounced: null, tracked: null, opened: null, deliveryRate: null, openRate: null }
  const sum = (k) => rows.reduce((a, d) => a + (Number(d && d[k]) || 0), 0)
  const sent = sum('emails_sent')
  const delivered = sum('emails_delivered')
  const tracked = sum('emails_tracked')
  const opened = sum('emails_opened')
  return {
    known: true,
    sent,
    delivered,
    bounced: sum('emails_bounced'),
    tracked,
    opened,
    deliveryRate: sent ? delivered / sent : null,
    openRate: tracked ? opened / tracked : null,
  }
}

/**
 * The tile values for sections A–D. Everything is derived from the same rows
 * the detail tables render. `null` means "not loaded" and the page prints a
 * dash with a reason — never a zero that could be read as "none".
 */
export function summarize({
  days = [],
  // THE STREAK IS NOT A RANGE STATISTIC, and on a one-day range that matters.
  //
  // Every other tile answers "what happened in the window you picked". A streak
  // answers "how long has the routine held", and a streak measured over a
  // window one day wide can only ever say 0 or 1 — so with the page's default
  // range (today) the headline tile would read "0 days" every morning before Bo
  // starts, which is a claim that the routine broke. It did not; the window was
  // simply too short to see it. So the caller may hand in a LONGER ledger to
  // count the streak over (Activity.jsx reads operation runs over a wider
  // window than it charts) and the tile says which window it used. When it is
  // absent the streak falls back to the range, exactly as before.
  streakDays = null,
  now = Date.now(),
  skipDays = [],
  summary = null, // eventsSummary() body
  copies = null, // draft_copied rows or null
  opens = null, // hubspot_email_opened rows or null
  metrics = null, // hubspotMetrics() body or null
  runs = [], // operation_run rows
} = {}) {
  const streakLedger = Array.isArray(streakDays) && streakDays.length ? streakDays : days
  const streak = streaks({ days: streakLedger, now, skipDays })
  const calendar = calendarStreak({ days: streakLedger, now, skipDays })
  const daysAllThree = days.filter((d) => d.allThree).length
  const questions = days.reduce((a, d) => a + (d.questions || 0), 0)
  const activeDays = days.filter((d) => (d.events || 0) > 0).length

  const ownCopies = Array.isArray(copies) ? copies.filter((r) => !isImpersonated(r)) : null
  const ownOpens = Array.isArray(opens) ? opens.filter((r) => !isImpersonated(r)) : null
  const drafts = {
    loaded: Boolean(ownCopies && ownOpens),
    companiesCopied: ownCopies ? uniq(ownCopies.map((r) => r.data && (r.data.key || r.data.companyId || r.data.company))).size : null,
    emailsCopied: ownCopies ? ownCopies.filter((r) => r.data && r.data.kind === 'email').length : null,
    subjectsCopied: ownCopies ? ownCopies.filter((r) => r.data && r.data.kind === 'subject').length : null,
    copies: ownCopies ? ownCopies.length : null,
    hubspotOpens: ownOpens ? ownOpens.length : null,
    hubspotOpenCompanies: ownOpens ? uniq(ownOpens.map((r) => r.data && (r.data.companyId || r.data.key || r.data.company))).size : null,
  }

  const t = (metrics && metrics.totals) || null
  const totalOf = (key) => (t && Object.prototype.hasOwnProperty.call(t, key) ? t[key] || 0 : null)
  const results = {
    loaded: Boolean(t),
    companiesReached: t ? t.outreach_companies || 0 : null,
    // Of those, the companies emailed for the very first time (touch 1).
    firstTouches: t ? t.first_touch_emails || 0 : null,
    outreachEmails: t ? t.outreach_emails || 0 : null,
    followups: t ? t.followup_emails || 0 : null,
    repliesSent: t ? t.replies_sent || 0 : null,
    responses: t ? t.responses || 0 : null,
    respondingCompanies: t ? t.responding_companies || 0 : null,
    // THE RATE IS ALL-TIME ON BOTH SIDES, and it has to be.
    //
    // A reply that lands inside the range is usually answering an email sent
    // BEFORE it — the cadence runs for weeks. Dividing this range's replying
    // companies by this range's FIRST touches therefore compares two different
    // cohorts, and it flatters wildly: 15 replies against 28 new companies
    // reads as "54% reply rate" when the honest all-time figure is 26 of 182,
    // or 14%. An investor checking that number would be right to stop reading.
    // So the percentage always comes from all_time (both numerator and
    // denominator), and the page says which companies it is a share of.
    companiesReachedAllTime: metrics && metrics.all_time ? metrics.all_time.outreach_companies || 0 : null,
    respondingCompaniesAllTime: metrics && metrics.all_time ? metrics.all_time.responding_companies || 0 : null,
    responseRate:
      metrics && metrics.all_time && metrics.all_time.outreach_companies
        ? (metrics.all_time.responding_companies || 0) / metrics.all_time.outreach_companies
        : null,
    deals: t ? t.deals || 0 : null,
    tasks: t ? t.tasks || 0 : null,
    // CALLS, MEETINGS AND NOTES — the mirror has always carried them and the
    // page showed none of it, which for a rep whose logged calls run into the
    // hundreds is a record that omits the work that matters most. Same rule as
    // every field above: the count is a number only when the mirror answered,
    // and null (a dash on the tile) when it did not. The `*Companies` figures
    // are the server's own uniques, not a count of the rows we were sent,
    // because the row lists are trimmed to a byte budget.
    //
    // These six ask for the KEY, not just the totals block, because they are
    // the only fields on this page that can outrun their own backend: until
    // the lambda that counts engagements is deployed, a perfectly healthy
    // mirror answers with totals that simply have no `calls` in them. `t.calls
    // || 0` would read that silence as "none", and print "Calls logged 0" for
    // a rep with 219 of them — a confident zero that announces nothing. The
    // server emits all six unconditionally (0 included) once it knows about
    // engagements, so an absent key means one thing only: nobody counted.
    calls: totalOf('calls'),
    callCompanies: totalOf('call_companies'),
    notes: totalOf('notes'),
    noteCompanies: totalOf('note_companies'),
    meetings: totalOf('meetings'),
    meetingCompanies: totalOf('meeting_companies'),
    // DELIVERY AND OPENS (2026-10-20) — the server's SENDS ledger: every email
    // the rep sent, bounces included, with HubSpot's tracker counts. Same
    // absent-key rule as the calls: a mirror lambda one deploy behind sends no
    // `emails_sent`, and these read null (a dash), never "0% delivered". A
    // rate is null on its own when its denominator is empty (no sends, or no
    // TRACKED delivered sends) — the server answers null there, not 0.
    emailsSent: totalOf('emails_sent'),
    emailsDelivered: totalOf('emails_delivered'),
    emailsBounced: totalOf('emails_bounced'),
    emailsTracked: totalOf('emails_tracked'),
    emailsOpened: totalOf('emails_opened'),
    emailsClicked: totalOf('emails_clicked'),
    deliveryRate: rateOf(t, 'delivery_rate'),
    openRate: rateOf(t, 'open_rate'),
    clickRate: rateOf(t, 'click_rate'),
    emailsBySource: t && t.emails_by_source && typeof t.emails_by_source === 'object' ? t.emails_by_source : null,
  }

  const dealRows = (metrics && metrics.rows && metrics.rows.deals) || []
  const taskRows = (metrics && metrics.rows && metrics.rows.tasks) || []
  const byAttr = (rows) => {
    const out = { owner: 0, creator: 0 }
    for (const r of rows) out[r.attributed_by === 'creator' ? 'creator' : 'owner'] += 1
    return out
  }

  const ownRuns = (runs || []).filter((r) => !isImpersonated(r))
  const runCounts = { total: ownRuns.length, built: 0, nothing: 0, unreadable: 0, failed: 0, aborted: 0, unknown: 0 }
  for (const r of ownRuns) runCounts[runOutcome(r)] += 1

  const signIns = days.reduce((a, d) => a + (d.signIns || 0), 0)
  const impersonatedEvents = days.reduce((a, d) => a + (d.impersonated || 0), 0)

  return {
    routine: {
      streak,
      calendar,
      daysAllThree,
      questions,
      questionsPerActiveDay: activeDays ? questions / activeDays : null,
      activeDays,
    },
    drafts,
    results,
    tracked: {
      deals: results.deals,
      dealsBy: byAttr(dealRows),
      tasks: results.tasks,
      tasksBy: byAttr(taskRows),
      runs: runCounts,
      signIns,
      activeDays,
      firstAt: (summary && summary.first_at) || null,
      lastAt: (summary && summary.last_at) || null,
      impersonatedEvents,
    },
  }
}

// ── what HubSpot recorded about the calls ───────────────────────────────────
//
// HubSpot returns `hs_call_disposition` and `hs_call_duration` EMPTY for every
// call this portal holds, so the sync drops them and the rows arrive without
// those keys at all. That is a fact about the CRM, not about the rep: rendering
// it as "0m" or as "no answer" would invent a finding, and rendering it as a
// bare dash with no explanation invites the reader to assume the page is broken.
// So the page says which of the two it is — and it can only say so from the
// rows it actually has, which is what this counts.
//
// Absent keys are the signal, per the payload contract: a field HubSpot did not
// record is OMITTED from the row rather than sent as '' or 0, so `duration_ms`
// present and 0 is a genuinely zero-length call and gets rendered as one.
//
// Returns null when there is no calls list to look at (mirror unread, or a
// mirror old enough not to ship one) — the caller then says nothing at all
// rather than making a claim about rows it never saw. `truncated` is the same
// caution one step down: the server trims long row lists to a byte budget, and
// "none of these calls has an outcome" is not a statement anyone can make about
// a list they were given part of.
export function callOutcomeCoverage(metrics) {
  const rows = metrics && metrics.rows && Array.isArray(metrics.rows.calls) ? metrics.rows.calls : null
  if (!rows) return null
  let withOutcome = 0
  let withDuration = 0
  for (const r of rows) {
    if (r && r.disposition != null && r.disposition !== '') withOutcome += 1
    if (r && r.duration_ms != null && r.duration_ms !== '') withDuration += 1
  }
  return {
    rows: rows.length,
    withOutcome,
    withDuration,
    truncated: Boolean(metrics.rows_truncated && metrics.rows_truncated.calls),
  }
}

// ── activity density ────────────────────────────────────────────────────────
//
// WHEN THE WORK ACTUALLY HAPPENS. Every HubSpot engagement the mirror holds
// (email, call, meeting, note) and every task and deal carries the instant it
// was stamped, so bucketing those instants answers the one question the tiles
// cannot: which hours of the day the rep is at the desk. The SERVER buckets
// (it holds the rows); the page may now ASK for a bucket — ?density_bucket=
// hour|day, which is what the reader's Hourly/Daily toggle sends — but it
// still cannot re-bucket what comes back, because a browser can sum hours into
// days and can never split days or weeks back into hours. So everything below
// only has to survive what the mirror actually sent, and to say so plainly
// when what it sent is not what was asked for (densityViewMismatchNote).
//
// EVERY FIELD ON THE WIRE IS OPTIONAL. The block is built by a lambda that
// deploys separately from this bundle, so a mirror one deploy behind answers
// with no `density` at all and a half-deployed one can answer with points but
// no kinds. Anything unusable becomes null here and the page prints
// "unavailable, not zero" — because the failure mode that matters is drawing
// an empty chart, which a reader takes for a quiet day rather than for a
// missing read.
//
// THE ONE EXCEPTION IS THE OPPOSITE CASE. When the mirror WAS read and
// genuinely holds nothing, the chart IS drawn: an axis of real zeros
// (densityAxis), with no sentence vouching for it — every read that failed gets
// a sentence in the chart's place, so a drawn chart already means it was read.
// "Unavailable" and "zero" remain two different facts with two different
// renderings; this is only the second of them finally getting a chart instead
// of a sentence, because a reader who asked about a quiet week deserves to see
// the week rather than a line of prose where the week should be.

// The buckets the wire can carry. 'hour' is 24 real hours of ONE day, each bar
// an instant on the clock. 'hourofday' is the PROFILE: exactly 24 bars keyed
// '00'..'23', where every 9 AM in the range shares one bar and the counts are
// summed across every day of the window — the only way to answer "which hours
// is this person at the desk" over anything longer than a day, and something
// no arithmetic in this file could recover from daily or weekly buckets.
// Its bars are not moments in time: they carry no `start`, nothing in them is
// "still filling", and the two rules that assume a bar IS a moment
// (densityFillingIndex, densityBucketEndMs) are guarded for it below.
export const DENSITY_BUCKETS = ['hour', 'hourofday', 'day', 'week']

// How many x-axis labels each bucket may print. A day of hours is 24 bars and
// 24 angled labels collide into a grey smear, so hours get roughly every
// third; days and weeks are sparse enough that most ranges print all of them.
// An hour-of-day profile is 24 bars as well, so it thins exactly like an hour.
const DENSITY_LABEL_TARGET = { hour: 8, hourofday: 8, day: 14, week: 14 }

// ── which view the reader asked for ─────────────────────────────────────────
//
// The BUCKET is the server's word for what it sent. The VIEW is the reader's
// word for what they asked to see, and the two are not the same thing: the
// server keeps picking its bucket from the span of the range unless the page
// asks for one, and a mirror one deploy behind ignores the request entirely
// and answers with whatever it always picked. So the page has to be able to
// tell "here is the hourly answer" from "here is a weekly answer to an hourly
// question", and to say the second one out loud instead of drawing it as
// though nobody had asked.
//
// ONLY TWO VIEWS ARE OFFERED. Weeks are still a bucket the server sends of its
// own accord for a long range, and every function below still renders one
// correctly — but no toggle asks for one, because a reader who wants the shape
// of a quarter gets it from daily bars.
export const DENSITY_VIEWS = ['hour', 'day']
export const DENSITY_VIEW_LABELS = { hour: 'Hourly', day: 'Daily' }

export function isDensityView(value) {
  return DENSITY_VIEWS.includes(value)
}

/**
 * The view a range opens on when the URL does not name one. `range` is a
 * rangeFor() frame.
 *
 * A single Chicago day opens HOURLY: "which hours" is the only question one
 * day can answer, and daily bars for it would be a chart with one bar in it.
 * Every longer window opens DAILY — including all-time, whose sinceDay is null
 * — because an hour-of-day profile is a claim about a person's habits and the
 * reader should ask for that rather than be handed it on arrival.
 */
export function defaultDensityView(range) {
  const since = range && range.sinceDay
  const until = range && range.untilDay
  return since && since === until ? 'hour' : 'day'
}

// The view actually in force: what the URL asked for when it asked for
// something this page offers, and the range's own default otherwise. A link
// somebody saved carrying ?density=week — or ?density=nonsense — therefore
// falls back to a real view instead of leaving the toggle pointing at a state
// no button on the page can undo.
export function densityViewOf(param, range) {
  return isDensityView(param) ? param : defaultDensityView(range)
}

// Does this payload actually answer the view the reader asked for? BOTH hour
// buckets answer the hourly view — one day's real hours, or the hour-of-day
// profile over a longer range — and only a day bucket answers the daily one. A
// weekly payload answers neither, and a payload that never arrived answers
// nothing at all, which is false rather than an error.
export function densityAnswersView(density, view) {
  const bucket = (density && density.bucket) || ''
  if (!bucket) return false
  if (view === 'hour') return bucket === 'hour' || bucket === 'hourofday'
  if (view === 'day') return bucket === 'day'
  return false
}

// The mirror honours ?density_bucket= only once the lambda that builds the
// block has been redeployed; until then it keeps choosing the bucket from the
// span of the range and the toggle moves nothing. Naming the parameter in the
// sentence is not a stray implementation detail — it is the difference between
// "this page is broken" and "this mirror is a deploy behind", and the person
// reading this page is usually the person who can ship it.
const DENSITY_REDEPLOY_HINT = '(The mirror answers ?density_bucket= once it is redeployed.)'

// One sentence per (bucket that arrived, view that was asked for) pair that can
// actually occur, in the same "unavailable — not zero" voice as the rest of the
// page. Spelled out rather than assembled from fragments because each one is a
// sentence a reader has to be able to act on, and they do not all have the same
// cause — see the hour-of-day pair.
const DENSITY_VIEW_MISMATCH = {
  'day:hour': `This HubSpot mirror bucketed the range by day rather than by hour, so the hourly view is unavailable — not zero. ${DENSITY_REDEPLOY_HINT}`,
  'week:hour': `This HubSpot mirror bucketed the range by week rather than by hour, so the hourly view is unavailable — not zero. ${DENSITY_REDEPLOY_HINT}`,
  'hour:day': `This HubSpot mirror bucketed the range by hour rather than by day, so the daily view is unavailable — not zero. ${DENSITY_REDEPLOY_HINT}`,
  'week:day': `This HubSpot mirror bucketed the range by week rather than by day, so the daily view is unavailable — not zero. ${DENSITY_REDEPLOY_HINT}`,
  // A mirror that CAN send an hour-of-day profile is a mirror that already
  // honours the parameter, so this pair is not a deploy behind: it is the
  // hourly answer still on screen while the daily one is being read. Blaming
  // the deploy here would send somebody off to redeploy a current lambda.
  'hourofday:day':
    'This is still the hour-of-day profile the hourly view asked for, not a count per day, so the daily view is unavailable — not zero, until the mirror answers again.',
}

/**
 * '' when the payload answers the view; otherwise the sentence to print INSTEAD
 * of the chart, naming what arrived and what was asked for.
 *
 * Drawing a weekly payload under a toggle set to Hourly would be a quieter
 * version of the lie this whole section refuses: the bars would be real, the
 * axis would be real, and the answer would be to a question the reader did not
 * ask — with nothing on screen to give it away.
 */
export function densityViewMismatchNote(density, view) {
  if (densityAnswersView(density, view)) return ''
  // A block that never arrived is not a mismatch: there is nothing to name,
  // and densityEmptyNote already takes the whole chart for it. Printing both
  // would state one failure twice, in two different voices.
  if (!density) return ''
  // Likewise a view this page does not offer. densityViewOf has already turned
  // anything unrecognised into a real view, so there is nothing to name here
  // either.
  if (!isDensityView(view)) return ''
  const known = DENSITY_VIEW_MISMATCH[`${density.bucket}:${view}`]
  if (known) return known
  // Unreachable through normalizeDensity, which only ever keeps a bucket from
  // DENSITY_BUCKETS. But a bucket this file has not been taught is still one we
  // cannot honestly present as the asked-for view, and naming it is cheaper
  // than returning '' and letting the chart imply it answered the question.
  const asked = DENSITY_VIEW_LABELS[view].toLowerCase()
  return `This HubSpot mirror bucketed the range by ${String(density.bucket || 'something it did not name')}, so the ${asked} view is unavailable — not zero.`
}

// ── the payload ─────────────────────────────────────────────────────────────

// A count off the wire: a non-negative finite number, else null. Null is not
// 0 — a missing count is a thing we were not told, and the callers below
// either derive it from something we WERE told or leave it out.
function densityCount(value) {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null
}

/**
 * A bucket start we can place on a real timeline: epoch milliseconds, or an
 * ISO string that carries its OWN utc offset ("…Z" or "…±HH:MM").
 *
 * A wall-clock string with no offset is refused rather than parsed. Date.parse
 * resolves "2026-08-28T17:00:00" in the VIEWER's zone, so a bucket the server
 * meant as 5 PM in Chicago becomes 5 PM in Tokyo for a reader sitting there —
 * the still-filling marker lands six hours out and every "not yet" bar
 * silently turns into a quiet one. Refusing it costs the marker its precision
 * (densityFillingIndex falls back to its no-start path); parsing it puts a
 * confident claim on the wrong hour, which is the failure this whole chart
 * exists to avoid. The server contract therefore REQUIRES the offset — see
 * hubspotMetrics in lib/hubspot.js.
 */
export function densityStartMs(value) {
  if (value == null || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const s = String(value).trim()
  if (/^\d{10,}$/.test(s)) return Number(s)
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(s)) return null
  const ms = Date.parse(s)
  return Number.isNaN(ms) ? null : ms
}

const HOUR_MS = 3600000

// When the bucket that STARTED at `startMs` runs out. An hour is always an
// hour of real time; a day and a week are read off the Chicago calendar, so
// the two DST days a year are 23 and 25 hours long rather than 24. null when
// the start cannot be placed on the calendar at all.
//
// THE ONE ASSUMPTION IN HERE: a day or week bucket is measured on the CHICAGO
// calendar whatever `tz` the mirror declares, because this app has one zone
// and is not gaining a timezone library for a boundary check. A mirror that
// bucketed elsewhere can therefore have its LAST day/week bucket called closed
// (or still open) up to a few hours early or late — and only that bucket, since
// every earlier one ends where the next one starts. Hours, the bucket the
// "which hours is the rep working" question actually uses, are exact.
function densityBucketEndMs(startMs, bucket) {
  if (startMs == null) return null
  if (bucket === 'hour') return startMs + HOUR_MS
  // AN HOUR-OF-DAY BAR IS NOT A STRETCH OF TIME. '3pm' holds every 3 PM in the
  // range, so there is no instant it runs out at and nothing for `now` to be
  // compared against; null is the only honest answer. Its points carry no
  // `start` either, so this branch is belt and braces — but a caller that
  // invents one must not be handed a confident end in return.
  if (bucket === 'hourofday') return null
  const day = chicagoDayOf(startMs)
  if (!day) return null
  return chicagoMidnightMs(addDays(day, bucket === 'week' ? 7 : 1))
}

/**
 * The /metrics `density` block, straightened out — or null when there is
 * nothing drawable in it (absent, not an object, or carrying no points array).
 * Null is the signal the page turns into "unavailable, not zero"; an object
 * with total 0 is the different, honest claim that the range holds no activity.
 */
export function normalizeDensity(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  if (!Array.isArray(raw.points)) return null

  const kinds = []
  const named = new Set()
  for (const k of Array.isArray(raw.kinds) ? raw.kinds : []) {
    const key = k && k.key != null && k.key !== '' ? String(k.key) : ''
    if (!key || named.has(key)) continue
    named.add(key)
    kinds.push({ key, label: k.label != null && k.label !== '' ? String(k.label) : key })
  }

  const points = []
  // Point key -> the bar already pushed under it. The key is the bar's REACT
  // key (Activity passes keyOf={(d) => d.key}), so a repeated one would make
  // React drop a bar and every count inside it; see the fold-in below.
  const byKey = new Map()
  for (const p of raw.points) {
    if (!p || typeof p !== 'object') continue
    // No key, no bar: the key is the bar's React identity and its tie to
    // `busiest`, and two unkeyed bars would collapse into one.
    const key = p.key != null && p.key !== '' ? String(p.key) : ''
    if (!key) continue
    const counts = {}
    let summed = 0
    for (const [k, v] of Object.entries(p.kinds && typeof p.kinds === 'object' ? p.kinds : {})) {
      const n = densityCount(v)
      if (!n) continue // a 0 or unreadable kind adds nothing to a breakdown
      counts[String(k)] = n
      summed += n
    }
    const declared = densityCount(p.total)
    // The LARGER of the declared total and the kinds it broke out — never the
    // declared one alone. The server may count a kind it did not break out, so
    // the declaration usually wins; but a half-written payload that declares 0
    // while still sending counts would otherwise draw a 0-height bar whose own
    // tooltip reads "6 emails". Both directions are the same rule: the bar is
    // never shorter than what we were told is inside it.
    const total = Math.max(declared == null ? 0 : declared, summed)
    const already = byKey.get(key)
    if (already) {
      // A REPEATED KEY IS NOT A SECOND BUCKET. Two bars keyed '09' cost us one
      // of them at render time, silently, taking its counts with it — so the
      // later point folds into the first instead of being drawn or dropped.
      already.total += total
      for (const [k, n] of Object.entries(counts)) already.kinds[k] = (already.kinds[k] || 0) + n
      continue
    }
    const point = {
      key,
      label: p.label != null && p.label !== '' ? String(p.label) : key,
      // Only a start we can actually place on a timeline is kept; an
      // offset-less wall-clock string is dropped rather than resolved in
      // whatever zone the reader is sitting in (densityStartMs).
      start: densityStartMs(p.start) == null ? '' : String(p.start),
      total,
      kinds: counts,
    }
    byKey.set(key, point)
    points.push(point)
  }

  // A kind that appears on a point but not in kinds[] still sits inside that
  // bar's total, so it gets an entry (named by its raw key) rather than being
  // dropped: a breakdown that does not add up to the bar is worse than an ugly
  // word.
  for (const p of points) {
    for (const k of Object.keys(p.kinds)) {
      if (named.has(k)) continue
      named.add(k)
      kinds.push({ key: k, label: k })
    }
  }

  // The server names the busiest bucket. When it does not — or names one that
  // is not among the points it sent — the tallest bar we actually have is the
  // honest answer, earliest bar winning a tie.
  let busiest = null
  const declaredKey = raw.busiest && raw.busiest.key != null ? String(raw.busiest.key) : ''
  const declared = declaredKey ? points.find((p) => p.key === declaredKey) : null
  if (declared) busiest = declared
  else for (const p of points) if (p.total > 0 && (!busiest || p.total > busiest.total)) busiest = p

  const declaredTotal = densityCount(raw.total)
  const summedTotal = points.reduce((a, p) => a + p.total, 0)
  return {
    bucket: DENSITY_BUCKETS.includes(raw.bucket) ? raw.bucket : 'day',
    tz: raw.tz ? String(raw.tz) : '',
    // Same rule as a point's total, for the same reason: a declared 0 sitting
    // over points that carry counts would suppress the chart entirely
    // (densityEmptyNote) and print "the mirror holds none" over data we were
    // sent. The larger number is the one we can defend.
    total: Math.max(declaredTotal == null ? 0 : declaredTotal, summedTotal),
    points,
    kinds,
    busiest: busiest ? { key: busiest.key, label: busiest.label, total: busiest.total } : null,
    // Rows the server could not place on the clock. Zero in practice (every
    // mirrored engagement carries hs_timestamp) but it is the reader's only
    // way to know the bars are the whole story, so it is never assumed.
    undatable: densityCount(raw.undatable) || 0,
  }
}

// The ceiling the server clips its own day series to. A chart with more bars
// than the plot has pixels is a smear, and an axis synthesised here has to stop
// exactly where a real one does — otherwise an empty range would draw MORE
// bars than the same range full of activity.
const DENSITY_MAX_POINTS = 400

// The hour as a twelve-hour clock word. The spaced form ('10 AM') is the one
// the mirror uses for its own hour buckets, so a day of real bars and a day of
// zeros carry the same axis and a reader flipping between ranges sees one
// style. The compact form ('10am') is for an hour-of-day profile, whose bars
// are hours of the clock rather than hours of a particular day.
function hourLabel(hour, compact = false) {
  const twelve = hour % 12 === 0 ? 12 : hour % 12
  if (compact) return `${twelve}${hour < 12 ? 'am' : 'pm'}`
  return `${twelve} ${hour < 12 ? 'AM' : 'PM'}`
}

// The hour (0–23) a Chicago clock reads at this instant, -1 when it cannot be
// read. NOT (ms − midnight) / an hour: on the two DST days a year that puts
// every bar after 2 AM on the wrong hour, which is the exact class of mistake
// the rest of this file goes out of its way to avoid.
function chicagoHourOf(ms) {
  try {
    const parts = {}
    for (const p of CHI_PARTS_FMT.formatToParts(new Date(ms))) parts[p.type] = p.value
    return Number(parts.hour) % 24
  } catch {
    return -1
  }
}

/**
 * THE ZERO AXIS, synthesised here in the browser.
 *
 * Used in exactly one situation: the mirror was read, it holds no activity at
 * all, and so it sent no points to draw. This page used to print a sentence in
 * the chart's place. A sentence is the right answer when we do not KNOW — a
 * read that failed, a mirror with no density block — and the wrong one when we
 * do: the range really is empty, and an axis of real zeros says that in the
 * same shape as every other range the reader has looked at.
 *
 * The result is shaped exactly like a normalizeDensity one so every function
 * below takes it without knowing where it came from. `tz` is '' because this
 * page does not know which zone the mirror would have bucketed in and will not
 * imply one.
 *
 * null when there is no axis to build — an all-time range (sinceDay null) has
 * no first day to count from — and a caller that gets null falls back to the
 * sentence it would have printed anyway.
 */
export function densityAxis({ view = '', sinceDay = '', untilDay = '' } = {}) {
  const since = isDayKey(sinceDay) ? String(sinceDay) : ''
  const until = isDayKey(untilDay) ? String(untilDay) : ''
  if (!since || !until || since > until) return null
  const frame = (bucket, points) => ({ bucket, tz: '', total: 0, points, kinds: [], busiest: null, undatable: 0 })

  if (view === 'hour') {
    // MORE THAN ONE DAY: the hour-of-day profile, which is 24 bars whatever the
    // window holds because every 9 AM in it shares a bar. No `start` on any of
    // them — the bar is not an instant, and inventing one would hand
    // densityFillingIndex a moment to mark as "still filling".
    if (since !== until) {
      const profile = Array.from({ length: 24 }, (_, h) => ({
        key: String(h).padStart(2, '0'),
        label: hourLabel(h, true),
        start: '',
        total: 0,
        kinds: {},
      }))
      return frame('hourofday', profile)
    }
    // ONE DAY: real hours with real instants, walked from that day's own
    // midnight to the next one rather than counted 0 through 23, because a
    // Chicago day is 23 or 25 hours long twice a year. The spring-forward day
    // has no 2 AM and honestly gets 23 bars; the fall-back day repeats 1 AM,
    // and the repeat is skipped rather than drawn twice — for the same reason
    // normalizeDensity folds a repeated key, since two bars keyed alike cost
    // one of them at render time.
    const start = chicagoMidnightMs(since)
    const end = chicagoMidnightMs(addDays(since, 1))
    if (start == null || end == null) return null
    const points = []
    const seen = new Set()
    for (let ms = start; ms < end; ms += HOUR_MS) {
      const hour = chicagoHourOf(ms)
      const day = chicagoDayOf(ms)
      if (hour < 0 || !day) continue
      const key = `${day}T${String(hour).padStart(2, '0')}`
      if (seen.has(key)) continue
      seen.add(key)
      points.push({ key, label: hourLabel(hour), start: ms, total: 0, kinds: {} })
    }
    return points.length ? frame('hour', points) : null
  }

  if (view === 'day') {
    // One bar per Chicago day, the most recent DENSITY_MAX_POINTS kept. The
    // walk STARTS at that ceiling instead of building the whole range and
    // slicing it, so a hand-edited ?from=1990-01-01 costs 400 steps rather than
    // thirteen thousand — the same bars either way.
    const first = addDays(until, -(DENSITY_MAX_POINTS - 1))
    const points = []
    for (let day = since > first ? since : first; day && day <= until; day = addDays(day, 1)) {
      points.push({ key: day, label: shortDay(day), start: chicagoMidnightMs(day), total: 0, kinds: {} })
    }
    return points.length ? frame('day', points) : null
  }

  // A view this page does not offer gets no invented axis. densityViewOf turns
  // anything unrecognised into a real view before it reaches here, so arriving
  // at this line means the caller asked for something that is not on the page.
  return null
}

// "9 emails", "1 note". The singular is taken ONLY when the payload proves it
// — when the label is its own key plus an 's' ("note" → "Notes") — so the app
// never guesses at English on a label the server chose. Anything else prints
// exactly as sent, "1 sms" and all: an awkward plural is a smaller lie than an
// invented singular.
function kindWord(n, key, label) {
  const word = String(label == null || label === '' ? key : label).toLowerCase()
  const singular = String(key || '').toLowerCase()
  return `${n.toLocaleString()} ${n === 1 && singular && word === `${singular}s` ? singular : word}`
}

/**
 * One bar's breakdown by kind — "12 emails · 3 calls · 1 note" — in the order
 * the payload lists its kinds, using the payload's own labels. '' when the
 * bucket holds nothing.
 */
export function densityBreakdown(point, kinds = []) {
  if (!point) return ''
  const counts = (point && point.kinds) || {}
  const list = Array.isArray(kinds) ? kinds : []
  const parts = []
  for (const k of list) if (counts[k.key]) parts.push(kindWord(counts[k.key], k.key, k.label))
  // Callers that hand in a partial kinds list still get every count that is
  // inside the bar; see the note in normalizeDensity.
  for (const [k, n] of Object.entries(counts)) {
    if (!n || list.some((x) => x.key === k)) continue
    parts.push(kindWord(n, k, k))
  }
  return parts.join(' · ')
}

/**
 * How to thin the x-axis: { every, offset } for BarChart's labelEvery /
 * labelOffset. The offset phases the thinning so the BUSIEST bucket is one of
 * the labelled ones — "which hour" is the question the chart exists to answer,
 * and an unlabelled tallest bar makes the reader count columns to find out.
 */
export function densityLabelPlan(density) {
  const points = (density && Array.isArray(density.points) && density.points) || []
  const target = DENSITY_LABEL_TARGET[density && density.bucket] || 14
  const every = Math.max(1, Math.ceil(points.length / target))
  if (every === 1) return { every: 1, offset: 0 }
  const at = density && density.busiest ? points.findIndex((p) => p.key === density.busiest.key) : -1
  return { every, offset: at >= 0 ? at % every : 0 }
}

/**
 * WHICH BAR IS THE ONE STILL FILLING — the last bucket that has already
 * started, from each point's own `start`. -1 when nothing is filling.
 *
 * Not simply "the last bar": a chart of today's hours may carry all 24 so the
 * axis is a whole day, in which case bars 11pm-ward are not a collapse in
 * effort, they are hours that have not happened. Marking the newest bar in
 * that payload would tell the reader the wrong thing about the wrong hour —
 * which is the exact misreading this marker exists to prevent.
 *
 *   endsToday  false for a range that finished (nothing is filling)
 *
 * STARTED IS NOT ENOUGH — the bucket also has to still be running. A server
 * that omits its empty buckets sends 8 AM, 9 AM, 11 AM and nothing else; at
 * 5 PM the newest bar has been closed for six hours, and calling it "still
 * filling" would excuse those six idle hours to the one reader asking whether
 * the rep was working. So a bucket is filling only while
 * `start <= now < end`, and -1 is the honest answer once the newest one has
 * closed. The end is the next bucket's start when we were sent one, capped at
 * the bucket's own length (an hour; a Chicago calendar day or week, so a DST
 * day is 23 or 25 hours) — whichever comes first.
 *
 * A payload with no readable `start` anywhere falls back to the newest bar,
 * because a server that sends no start is one we can only assume trimmed its
 * points to the present.
 */
export function densityFillingIndex(density, { endsToday = false, now = Date.now() } = {}) {
  const points = (density && Array.isArray(density.points) && density.points) || []
  if (!endsToday || !points.length) return -1
  const bucket = (density && density.bucket) || 'day'
  // AN HOUR-OF-DAY PROFILE HAS NO BAR STILL FILLING, whatever the clock says.
  // The 11pm bar holds every 11pm in the range, nearly all of them on days that
  // finished long ago; the one hour of it that is genuinely in progress is a
  // sliver of a bar that is otherwise complete. And because those points carry
  // no `start`, the no-readable-start fallback below would otherwise mark the
  // LAST bar — telling the reader 11pm is live at 9 in the morning, which is
  // precisely the misreading this marker exists to prevent.
  if (bucket === 'hourofday') return -1
  const starts = points.map((p) => densityStartMs(p.start))
  if (!starts.some((ms) => ms != null)) return points.length - 1
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const ms = starts[i]
    if (ms == null || ms > now) continue
    // The newest bucket that has started. Whether anything is filling at all
    // is decided here and nowhere else: an earlier bucket cannot be in
    // progress once a later one has begun.
    let end = densityBucketEndMs(ms, bucket)
    for (let j = i + 1; j < points.length; j += 1) {
      const next = starts[j]
      if (next == null) continue
      end = end == null ? next : Math.min(end, next)
      break
    }
    return end == null || now < end ? i : -1
  }
  return -1
}

/**
 * BarChart rows for the density chart. `fillingAt` (from densityFillingIndex)
 * marks the bucket in progress, and everything after it as not yet reached —
 * so neither a half-finished hour nor an hour that has not happened reads as a
 * rep who stopped working.
 */
export function densityBars(density, { fillingAt = -1 } = {}) {
  const points = (density && Array.isArray(density.points) && density.points) || []
  return points.map((p, i) => {
    const parts = [densityBreakdown(p, (density && density.kinds) || [])]
    const notYet = fillingAt >= 0 && i > fillingAt
    if (fillingAt >= 0 && i === fillingAt) parts.push('still filling')
    else if (notYet) parts.push('not yet')
    return {
      key: p.key,
      label: p.label,
      value: p.total,
      tooltip: p.label,
      // BarChart draws no rect for a 0, so an hour that has not happened would
      // be blank space — pixel-identical to an hour the rep skipped, on a
      // chart whose whole job is telling those two apart. The flag gets it a
      // faint band instead, so the difference is visible without a hover.
      notYet,
      // Never blank: an empty sub would leave the tooltip saying only the hour
      // and a bold 0, which reads as a rendering fault rather than a quiet hour.
      sub: parts.filter(Boolean).join(' · ') || 'no activity',
    }
  })
}

// The line to print INSTEAD of the chart. '' means draw the chart. The cases
// left here are deliberately different sentences, because a read that failed,
// a mirror that shipped no density block, and a mirror that counted activity
// it then placed in no bucket are three different facts — and NONE of them is
// a zero. Every one of them is something we were not told.
//
// A RANGE THAT GENUINELY HOLDS NOTHING NO LONGER LANDS HERE. It gets a chart
// of real zeros instead of a sentence; the one sentence left for it — activity
// that exists but carries no timestamp — is densityZeroCaption's, printed with
// the chart rather than in place of it. The distinction this section is built
// on is unchanged: an empty chart is a lie only when we do not know the range
// is empty. Here we do know, so the honest rendering is the chart.
//
// A caller that gets '' from here and holds a payload with NO POINTS has to
// substitute densityAxis: '' means "a chart is the right answer", never "this
// payload can draw itself".
export function densityEmptyNote(density, { resultsOk = false, loading = false } = {}) {
  if (loading) return 'Reading the HubSpot mirror…'
  if (!resultsOk) return 'HubSpot activity is unavailable — not zero.'
  if (!density) return 'This HubSpot mirror answered without an activity-density block, so the hours are unavailable — not zero.'
  // What is drawable is decided by the BARS, not by the header count: a
  // payload whose points carry activity draws whatever its `total` says, and a
  // payload whose header counts activity it never placed in a bucket is not a
  // quiet range — it is a mirror we cannot chart, which is the case below.
  const placed = density.points.reduce((a, p) => a + p.total, 0)
  if (placed > 0) return ''
  if (density.total > placed) {
    return `The mirror counted ${density.total.toLocaleString()} ${
      density.total === 1 ? 'activity' : 'activities'
    } in this range but placed none of them in a bucket, so the hours are unavailable — not zero.`
  }
  // Everything from here down is a genuine zero, and a genuine zero gets a
  // chart of zeros — see the note above.
  return ''
}

/**
 * The caption for a chart whose every bar is a zero, in the one case where the
 * zeros are not the whole story: activity the mirror counted but could not put
 * on the clock. '' otherwise — as soon as the payload places any activity, for
 * the cases densityEmptyNote takes the whole chart for (so the page can never
 * print both a replacement sentence and a caption about the same range), and
 * for a range that simply holds nothing.
 *
 * That last case used to say "No HubSpot activity in this range — the mirror
 * was read and it holds none. Every bar below is a real zero." The user asked
 * for it gone, and nothing is lost: the flat bars say it, and a drawn chart
 * already means the mirror was read, because every read that failed gets
 * densityEmptyNote's sentence in the chart's place instead.
 */
export function densityZeroCaption(density) {
  if (!density) return ''
  const points = (Array.isArray(density.points) && density.points) || []
  const placed = points.reduce((a, p) => a + p.total, 0)
  if (placed > 0) return ''
  // Counted but bucketed nowhere is not a zero, and densityEmptyNote has
  // already replaced the chart for it; a caption here would contradict it.
  if (density.total > placed) return ''
  if (density.undatable > 0) {
    return `No HubSpot activity could be placed on the clock in this range — ${density.undatable.toLocaleString()} ${
      density.undatable === 1 ? 'activity carries' : 'activities carry'
    } no timestamp.`
  }
  return ''
}
